// src/components/MetodoPagamentoForm.jsx

import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Typography,
  Paper,
  Divider,
  Alert,
  FormHelperText,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { formatarMoeda } from '../../utils/formatters';
import { PaymentMethodsConfig } from '../../utils/paymentMethods';
import api from '../../services/api';
import { useNotificacao } from '../../contexts/NotificacaoContext';

// Componentes estilizados
const PaymentButton = styled(Button)(({ method }) => ({
  backgroundColor: PaymentMethodsConfig[method]?.buttonColor || '#2e7d32',
  color: 'white',
  padding: '10px 20px',
  width: '100%',
  maxWidth: 300,
  '&:hover': {
    backgroundColor: PaymentMethodsConfig[method]?.buttonColor || '#1b5e20',
    opacity: 0.9,
  },
  marginTop: 8,
}));

const MethodLogoTop = styled('img')({
  maxWidth: 180,
  display: 'block',
  margin: '10px auto',
});

const MetodoPagamentoForm = ({ 
  prestacao, 
  onMetodoPagamentoChange, 
  metodoPagamento,
  error,
  valorTotal,
  modoPublico = false
}) => {
  const [dadosPagamento, setDadosPagamento] = useState({
    numeroTelefone: '',
    entidade: '',
    referencia: '',
    numero: '',
    validade: '',
    cvv: ''
  });

  const [validacaoLocal, setValidacaoLocal] = useState({
    numeroTelefone: '',
    entidade: '',
    referencia: '',
    numero: '',
    validade: '',
    cvv: ''
  });

  const { adicionarNotificacao } = useNotificacao();

  const handleVisualizarFatura = async () => {
    try {
      const response = await api.get(`/pagamentos/fatura-proforma/${prestacao.id}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`
        },
        responseType: 'blob'
      });
      
      const pdfUrl = window.URL.createObjectURL(new Blob([response.data]));
      window.open(pdfUrl, '_blank');
    } catch (error) {
      console.error('Erro ao visualizar fatura:', error);
      adicionarNotificacao('Erro ao carregar fatura', 'error');
    }
  };

  const validarNumeroTelefone = (numero) => {
    if (!numero) return false;
    const numeroLimpo = numero.replace(/\D/g, '');
    return /^84\d{7}$/.test(numeroLimpo);
  };

  const validarEntidade = (entidade) => {
    return /^\d{5}$/.test(entidade);
  };

  const validarReferencia = (referencia) => {
    return /^\d{9}$/.test(referencia);
  };

  const validarCartao = (numero, validade, cvv) => {
    const numeroValido = /^\d{16}$/.test(numero.replace(/\s/g, ''));
    const validadeValida = /^(0[1-9]|1[0-2])\/([0-9]{2})$/.test(validade);
    const cvvValido = /^\d{3,4}$/.test(cvv);
    
    if (!numeroValido) return 'Número de cartão inválido';
    if (!validadeValida) return 'Data de validade inválida (MM/AA)';
    if (!cvvValido) return 'CVV inválido';
    return '';
  };

  const getNumeroMensagemErro = (numero, metodo) => {
    if (!numero) return '';
    if (numero.replace(/\D/g, '').length < 9) return 'Número incompleto';
    if (!numero.match(/^(84|85|86|87)/)) {
      return metodo === 'MPESA' ? 'Deve começar com 84' : 'Deve começar com 86';
    }
    if (!validarNumeroTelefone(numero)) return 'Número inválido';
    return '';
  };

  const handleDadosChange = (campo, valor) => {
    let valorFormatado = valor;

    switch (campo) {
      case 'numeroTelefone':
      case 'entidade':
      case 'referencia':
      case 'cvv':
        valorFormatado = valor.replace(/\D/g, '');
        break;
      
      case 'numero':
        valorFormatado = valor.replace(/\D/g, '')
          .replace(/(\d{4})/g, '$1 ')
          .trim();
        break;
        
      case 'validade':
        valorFormatado = valor
          .replace(/\D/g, '')
          .replace(/(\d{2})(\d)/, '$1/$2')
          .substring(0, 5);
        break;
      default:
        break;
    }

    const limites = {
      numeroTelefone: 9,
      entidade: 5,
      referencia: 9,
      numero: 19,
      validade: 5,
      cvv: 4
    };

    if (limites[campo] && valorFormatado.length > limites[campo]) {
      valorFormatado = valorFormatado.slice(0, limites[campo]);
    }

    const novosDados = { ...dadosPagamento, [campo]: valorFormatado };
    setDadosPagamento(novosDados);

    let mensagemErro = '';
    
    switch (campo) {
      case 'numeroTelefone':
        mensagemErro = getNumeroMensagemErro(valorFormatado, metodoPagamento);
        break;
      case 'entidade':
        mensagemErro = validarEntidade(valorFormatado) ? '' : 'Entidade inválida';
        break;
      case 'referencia':
        mensagemErro = validarReferencia(valorFormatado) ? '' : 'Referência inválida';
        break;
      case 'numero':
      case 'validade':
      case 'cvv':
        if (metodoPagamento === 'CARTAO') {
          mensagemErro = validarCartao(
            novosDados.numero,
            novosDados.validade,
            novosDados.cvv
          );
        }
        break;
      default:
        break;
    }

    setValidacaoLocal(prev => ({
      ...prev,
      [campo]: mensagemErro
    }));

    const dadosParaEnvio = {
      metodoPagamento,
      dadosPagamento: {
        valorPago: valorTotal || prestacao.valorComMulta || prestacao.valor
      }
    };

    switch (metodoPagamento) {
      case 'MPESA':
      case 'EMOLA':
        if (validarNumeroTelefone(valorFormatado)) {
          dadosParaEnvio.dadosPagamento.numeroTelefone = valorFormatado;
          onMetodoPagamentoChange(dadosParaEnvio);
        }
        break;

      case 'BCI':
      case 'STANDARDBANK':
      case 'MOZABANK':
        if (validarEntidade(novosDados.entidade) && validarReferencia(novosDados.referencia)) {
          dadosParaEnvio.dadosPagamento = {
            ...dadosParaEnvio.dadosPagamento,
            entidade: novosDados.entidade,
            referencia: novosDados.referencia
          };
          onMetodoPagamentoChange(dadosParaEnvio);
        }
        break;

      case 'CARTAO':
        const validacaoCartao = validarCartao(novosDados.numero, novosDados.validade, novosDados.cvv);
        if (validacaoCartao === '') {
          dadosParaEnvio.dadosPagamento = {
            ...dadosParaEnvio.dadosPagamento,
            numero: novosDados.numero.replace(/\s/g, ''),
            validade: novosDados.validade,
            cvv: novosDados.cvv
          };
          onMetodoPagamentoChange(dadosParaEnvio);
        }
        break;
      default:
        break;
    }
  };

  const getNumeroPlaceholder = (metodo) => {
    switch (metodo) {
      case 'MPESA': return '84XXXXXXX';
      case 'EMOLA': return '86XXXXXXX';
      default: return '';
    }
  };

  return (
    <Paper elevation={0} sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6">
          Método de Pagamento
        </Typography>
        {!modoPublico && (
          <Button 
            variant="outlined"
            onClick={handleVisualizarFatura}
          >
            Visualizar Fatura
          </Button>
        )}
      </Box>

      {modoPublico && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Link válido por 7 dias. Para mais opções, acesse seu dashboard.
        </Alert>
      )}

      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle1" color="primary" gutterBottom>
          Valor a Pagar: {formatarMoeda(valorTotal || prestacao.valorComMulta || prestacao.valor)}
        </Typography>
        {prestacao.valorComMulta > prestacao.valor && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Este valor inclui multa por atraso de {formatarMoeda(prestacao.valorComMulta - prestacao.valor)}
          </Alert>
        )}
      </Box>

      <Divider sx={{ my: 2 }} />

      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <FormControl fullWidth sx={{ minWidth: 250, mb: 2 }}>
          <InputLabel id="metodo-pagamento-label">Selecione o Método de Pagamento</InputLabel>
          <Select
            labelId="metodo-pagamento-label"
            value={metodoPagamento}
            label="Selecione o Método de Pagamento"
            onChange={(event) => {
              const selectedMethod = event.target.value;
              onMetodoPagamentoChange({ 
                metodoPagamento: selectedMethod, 
                dadosPagamento: null 
              });
              setDadosPagamento({
                numeroTelefone: '',
                entidade: '',
                referencia: '',
                numero: '',
                validade: '',
                cvv: ''
              });
              setValidacaoLocal({
                numeroTelefone: '',
                entidade: '',
                referencia: '',
                numero: '',
                validade: '',
                cvv: ''
              });
            }}
          >
            <MenuItem value="" disabled>
              Selecione o Método de Pagamento
            </MenuItem>
            {Object.entries(PaymentMethodsConfig).map(([key, config]) => (
              <MenuItem key={key} value={key}>
                {config.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {metodoPagamento && (
          <>
            <MethodLogoTop
              src={PaymentMethodsConfig[metodoPagamento]?.buttonLogo}
              alt={PaymentMethodsConfig[metodoPagamento]?.name}
            />
            {['MPESA', 'EMOLA'].includes(metodoPagamento) && (
              <Box sx={{ pl: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  label={metodoPagamento === 'MPESA' ? "Número M-PESA" : "Número E-MOLA"}
                  value={dadosPagamento.numeroTelefone}
                  onChange={(e) => handleDadosChange('numeroTelefone', e.target.value)}
                  error={!!validacaoLocal.numeroTelefone}
                  helperText={validacaoLocal.numeroTelefone || `Formato: ${getNumeroPlaceholder(metodoPagamento)}`}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">+258</InputAdornment>
                  }}
                  sx={{ mb: 1 }}
                />
              </Box>
            )}

            {['BCI', 'STANDARDBANK', 'MOZABANK'].includes(metodoPagamento) && (
              <Box sx={{ pl: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Entidade"
                  value={dadosPagamento.entidade}
                  onChange={(e) => handleDadosChange('entidade', e.target.value)}
                  error={!!validacaoLocal.entidade}
                  helperText={validacaoLocal.entidade || "5 dígitos"}
                  sx={{ mb: 1 }}
                />
                <TextField
                  fullWidth
                  label="Referência"
                  value={dadosPagamento.referencia}
                  onChange={(e) => handleDadosChange('referencia', e.target.value)}
                  error={!!validacaoLocal.referencia}
                  helperText={validacaoLocal.referencia || "9 dígitos"}
                />
              </Box>
            )}

            {metodoPagamento === 'CARTAO' && (
              <Box sx={{ pl: 4, mt: 1 }}>
                <TextField
                  fullWidth
                  label="Número do Cartão"
                  value={dadosPagamento.numero}
                  onChange={(e) => handleDadosChange('numero', e.target.value)}
                  error={!!validacaoLocal.numero}
                  helperText={validacaoLocal.numero || "16 dígitos"}
                  sx={{ mb: 1 }}
                />
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <TextField
                    label="Validade"
                    value={dadosPagamento.validade}
                    onChange={(e) => handleDadosChange('validade', e.target.value)}
                    error={!!validacaoLocal.validade}
                    helperText={validacaoLocal.validade || "MM/AA"}
                    placeholder="MM/AA"
                    sx={{ width: '50%' }}
                  />
                  <TextField
                    label="CVV"
                    value={dadosPagamento.cvv}
                    onChange={(e) => handleDadosChange('cvv', e.target.value)}
                    error={!!validacaoLocal.cvv}
                    helperText={validacaoLocal.cvv || "3-4 dígitos"}
                    type="password"
                    sx={{ width: '50%' }}
                  />
                </Box>
              </Box>
            )}
          </>
        )}
      </Box>

      {error && (
        <Alert 
          severity="error" 
          sx={{ mt: 2 }}
          action={modoPublico && (
            <Button 
              color="inherit" 
              size="small"
              href={`${process.env.FRONTEND_URL}/dashboard`}
            >
              Ir para Dashboard
            </Button>
          )}
        >
          {error}
        </Alert>
      )}
    </Paper>
  );
};

MetodoPagamentoForm.propTypes = {
  prestacao: PropTypes.shape({
    id: PropTypes.number.isRequired,
    valor: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
    valorComMulta: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    mes: PropTypes.string.isRequired
  }).isRequired,
  onMetodoPagamentoChange: PropTypes.func.isRequired,
  metodoPagamento: PropTypes.string,
  error: PropTypes.string,
  valorTotal: PropTypes.number,
  modoPublico: PropTypes.bool
};

export default MetodoPagamentoForm;
